
import { createScorer } from '@mastra/core/evals';
import type { MastraModelConfig } from '@mastra/core/llm';

import { z } from 'zod/v4';
import { getAssistantMessageFromRunOutput, roundToTwoDecimals } from '../../utils';
import type { ScorerRunInputForLLMJudge, ScorerRunOutputForLLMJudge } from '../../utils';
import {
  BIAS_AGENT_INSTRUCTIONS,
  createBiasAnalyzePrompt,
  createBiasExtractPrompt,
  createBiasReasonPrompt,
} from './prompts';

export interface BiasMetricOptions {
  scale?: number;
}

export function createBiasScorer({ model, options }: { model: MastraModelConfig; options?: BiasMetricOptions }) {
  return createScorer<ScorerRunInputForLLMJudge, ScorerRunOutputForLLMJudge>({
    id: 'bias-scorer',
    name: 'Bias Scorer',
    description: 'A scorer that evaluates the bias of an LLM output to an input',
    judge: {
      model,
      instructions: BIAS_AGENT_INSTRUCTIONS,
    },
    type: 'agent',
  })
    .preprocess({
      description: 'Extract relevant statements from the LLM output',
      outputSchema: {
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "type": "object",
  "properties": {
    "opinions": {
      "type": "array",
      "items": {
        "type": "string"
      }
    }
  },
  "required": [
    "opinions"
  ]
},
      createPrompt: ({ run }) =>
        createBiasExtractPrompt({ output: getAssistantMessageFromRunOutput(run.output) ?? '' }),
    })
    .analyze({
      description: 'Score the relevance of the statements to the input',
      outputSchema: {
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "type": "object",
  "properties": {
    "results": {
      "type": "array",
      "items": {
        "type": "object",
        "properties": {
          "result": {
            "type": "string"
          },
          "reason": {
            "type": "string"
          }
        },
        "required": [
          "result",
          "reason"
        ]
      }
    }
  },
  "required": [
    "results"
  ]
},
      createPrompt: ({ run, results }) => {
        const prompt = createBiasAnalyzePrompt({
          output: getAssistantMessageFromRunOutput(run.output) ?? '',
          opinions: results.preprocessStepResult?.opinions || [],
        });
        return prompt;
      },
    })
    .generateScore(({ results }) => {
      if (!results.analyzeStepResult || results.analyzeStepResult.results.length === 0) {
        return 0;
      }

      const biasedVerdicts = results.analyzeStepResult.results.filter(v => v.result.toLowerCase() === 'yes');

      const score = biasedVerdicts.length / results.analyzeStepResult.results.length;
      return roundToTwoDecimals(score * (options?.scale || 1));
    })
    .generateReason({
      description: 'Reason about the results',
      createPrompt: ({ score, results }) => {
        return createBiasReasonPrompt({
          score,
          biases: results.analyzeStepResult?.results.map(v => v.reason) || [],
        });
      },
    });
}
